/**
 * Copyright 2015 GeoSolutions Sas
 * Copyright 2016-2021 Sourcepole AG
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */

import ol from 'openlayers';
import {loadFeaturesXhr} from '../utils/FeatureLoader';
import {VOID} from 'ol/functions.js';
import FeatureStyles from 'qwc2/utils/FeatureStyles';
import FeatureRegistry from "../utils/FeatureRegistry";
import CoordinatesUtils from "qwc2/utils/CoordinatesUtils";
import {createXYZ} from "ol/tilegrid";
import SldUtils from "../utils/SldUtils";
import LayerScaleUtils from "../utils/LayerScaleUtils";

function createVectorSource(options, map) {
    const format = new ol.format.GeoJSON();

    // try to keep the links
    format._origReadFeatureFromObject = format.readFeatureFromObject;
    format.readFeatureFromObject = function (object, opt_options) {
        const feature = this._origReadFeatureFromObject(object, opt_options);
        feature.links = object.links;
        return feature;
    };

    const source = new ol.source.Vector({
        strategy: function (extent, resolution) {
            const tileGrid = createXYZ({
                tileSize: 1024
            });
            const z = tileGrid.getZForResolution(resolution);
            const tileRange = tileGrid.getTileRangeForExtentAndZ(extent, z);
            const extents = [];
            const tileCoord = [z, 0, 0];
            for (
                tileCoord[1] = tileRange.minX;
                tileCoord[1] <= tileRange.maxX;
                ++tileCoord[1]
            ) {
                for (
                    tileCoord[2] = tileRange.minY;
                    tileCoord[2] <= tileRange.maxY;
                    ++tileCoord[2]
                ) {
                    extents.push(tileGrid.getTileCoordExtent(tileCoord));
                }
            }
            if (z > tileGrid.getMinZoom()) {
                const tileRange = tileGrid.getTileRangeForExtentAndZ(extent, z - 1);
                const tileCoord = [z - 1, 0, 0];
                for (
                    tileCoord[1] = tileRange.minX;
                    tileCoord[1] <= tileRange.maxX;
                    ++tileCoord[1]
                ) {
                    for (
                        tileCoord[2] = tileRange.minY;
                        tileCoord[2] <= tileRange.maxY;
                        ++tileCoord[2]
                    ) {
                        this.removeLoadedExtent(tileGrid.getTileCoordExtent(tileCoord));
                    }
                }
            }
            return extents;
        },
        loader: function (extent, resolution, projection, success, failure) {
            const separator = (options.url.indexOf('?') > 0) ? '&' : '?';
            extent = CoordinatesUtils.reprojectBbox(extent, map.getView().getProjection().getCode(), options.projection);
            let url = options.url + separator + 'bbox=' + extent.join(',') + '&crs=' + options.projection + '&bbox-crs=' + options.projection;
            if (options.featureLimit) {
                url += '&limit=' + options.featureLimit;
            }
            loadFeaturesXhr(url, format, extent, resolution, projection, options.auth,
                function (features, dataProjection) {
                    features.forEach(feature => {
                        //if (feature.crs && feature.crs !== options.projection) {
                        //feature.getGeometry().transform(feature.crs, options.projection);
                        //}
                        feature.setId(feature.getProperties().id);
                        FeatureRegistry[feature.getProperties().id] = feature;
                    });
                    source.addFeatures(features);
                    if (success !== undefined) {
                        success(features);
                    }
                },
                failure ? failure : VOID);
        },
        format: format
    });
    return source;
};

export default {
    create: (options, map) => {
        const source = createVectorSource(options, map);

        let styleFunction = feature => {
            const styleName = options.styleName || 'default';
            const styleOptions = options.styleOptions || {};
            return FeatureStyles[styleName](feature, styleOptions);
        };
        if (options.layoutFunction) {
            styleFunction = feature => {
                return options.layoutFunction(feature, options);
            };
        }

        const vectorLayer = new ol.layer.Vector({
            msId: options.id,
            source: source,
            style: styleFunction,
            ...LayerScaleUtils.getResolutionRange(options, map)
        });

        SldUtils.applyExternalSld(map, vectorLayer, options);
        return vectorLayer;
    },

    update: (layer, newOptions, oldOptions, map) => {
        if (newOptions.minScale !== oldOptions.minScale || newOptions.maxScale !== oldOptions.maxScale) {
            const {minResolution, maxResolution} = LayerScaleUtils.getResolutionRange(newOptions, map);
            layer.setMinResolution(minResolution ?? 0);
            layer.setMaxResolution(maxResolution ?? Infinity);
        }
        if (newOptions.filterLevel || oldOptions.filterLevel || newOptions.layerFilters || oldOptions.layerFilters) {
            const source = createVectorSource(newOptions, map);
            layer.setSource(source);
        } else if (newOptions.rev !== oldOptions.rev) {
            // drops the loaded features and loaded extents, so features deleted on the server disappear
            layer.getSource().refresh();
        }
    }
};
